export const STATUS_SESSION_EXPIRED_EVENT = 'status:session-expired';
export const TENANT_OIDC_REAUTHENTICATION_CODE =
  'authentication.tenant_oidc_reauthentication_required';

const AUTH_API_PREFIX = '/api/v1/auth/';
const PUBLIC_API_PREFIX = '/api/v1/public/';
const STATUS_API_PREFIX = '/api/v1/status/';
const REAUTHENTICATION_STORAGE_KEY = 'app-status:reauthentication-attempt';
const REAUTHENTICATION_RETRY_AFTER_MS = 5 * 60 * 1000;

type ReauthenticationAttempt = {
  returnUrl: string;
  attemptedAt: number;
};

function requestUrl(input: RequestInfo | URL): URL | null {
  try {
    if (input instanceof Request) {
      return new URL(input.url, window.location.href);
    }
    return new URL(String(input), window.location.href);
  } catch {
    return null;
  }
}

export function isProtectedStatusApiRequest(input: RequestInfo | URL): boolean {
  const url = requestUrl(input);
  return Boolean(
    url &&
      url.origin === window.location.origin &&
      url.pathname.startsWith('/api/') &&
      !url.pathname.startsWith(AUTH_API_PREFIX) &&
      !url.pathname.startsWith(PUBLIC_API_PREFIX)
  );
}

function isStatusDataRequest(input: RequestInfo | URL): boolean {
  const url = requestUrl(input);
  return Boolean(url && url.origin === window.location.origin && url.pathname.startsWith(STATUS_API_PREFIX));
}

async function responseRequiresReauthentication(response: Response): Promise<boolean> {
  if (response.status === 401) return true;
  if (response.status !== 403) return false;
  try {
    const problem = (await response.clone().json()) as {
      errors?: { code?: unknown; reauthenticate?: unknown };
    };
    return (
      problem.errors?.code === TENANT_OIDC_REAUTHENTICATION_CODE &&
      problem.errors.reauthenticate === true
    );
  } catch {
    return false;
  }
}

export function clearStatusReauthenticationAttempt(): void {
  try {
    window.sessionStorage.removeItem(REAUTHENTICATION_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in hardened browser contexts. In-memory redirect
    // suppression in App still prevents duplicate navigation in the current page.
  }
}

export function claimStatusReauthentication(returnUrl: string, now = Date.now()): boolean {
  try {
    const stored = window.sessionStorage.getItem(REAUTHENTICATION_STORAGE_KEY);
    if (stored) {
      const previous = JSON.parse(stored) as Partial<ReauthenticationAttempt>;
      if (
        previous.returnUrl === returnUrl &&
        typeof previous.attemptedAt === 'number' &&
        now - previous.attemptedAt < REAUTHENTICATION_RETRY_AFTER_MS
      ) {
        return false;
      }
    }
    window.sessionStorage.setItem(
      REAUTHENTICATION_STORAGE_KEY,
      JSON.stringify({ returnUrl, attemptedAt: now } satisfies ReauthenticationAttempt)
    );
  } catch {
    // Continue with one in-memory guarded redirect when storage is unavailable.
  }
  return true;
}

export function installStatusSessionExpiryInterceptor(): () => void {
  const browserFetch = window.fetch.bind(window);
  let reported = false;
  const interceptedFetch: typeof window.fetch = async (input, init) => {
    const response = await browserFetch(input, init);
    if (response.ok && isStatusDataRequest(input)) {
      reported = false;
      clearStatusReauthenticationAttempt();
      return response;
    }
    if (
      !reported &&
      isProtectedStatusApiRequest(input) &&
      (await responseRequiresReauthentication(response))
    ) {
      reported = true;
      window.dispatchEvent(new Event(STATUS_SESSION_EXPIRED_EVENT));
    }
    return response;
  };

  window.fetch = interceptedFetch;
  return () => {
    if (window.fetch === interceptedFetch) {
      window.fetch = browserFetch;
    }
  };
}
